import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../../lib/analytics";

type MetaDefinition = {
  title: string;
  description: string;
};

const SHARED_IMAGE_PATH = "/images/FSM_logo.png";

function upsertMeta(
  attr: "name" | "property",
  key: string,
  content: string,
): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attr}='${key}']`);

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

export default function RouteMetadata() {
  const { pathname, search } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // Tell the prerender service that the page is not yet ready.
    // The service polls this flag and only captures the HTML once it is true,
    // ensuring crawlers receive the correct per-route Open Graph meta tags.
    (window as Window & { prerenderReady?: boolean }).prerenderReady = false;

    const params = new URLSearchParams(search);
    const catalogType = params.get("type");

    let copy: MetaDefinition = {
      title: t("seo.default.title"),
      description: t("seo.default.description"),
    };

    if (pathname === "/profile") {
      copy = {
        title: t("seo.profile.title"),
        description: t("seo.profile.description"),
      };
    } else if (pathname === "/settings/account") {
      copy = {
        title: t("seo.settingsAccount.title"),
        description: t("seo.settingsAccount.description"),
      };
    } else if (pathname === "/catalog" && catalogType === "fixed") {
      copy = {
        title: t("seo.catalogFixed.title"),
        description: t("seo.catalogFixed.description"),
      };
    } else if (pathname === "/catalog" && catalogType === "auction") {
      copy = {
        title: t("seo.catalogAuction.title"),
        description: t("seo.catalogAuction.description"),
      };
    }

    const canonicalUrl = `${window.location.origin}${pathname}${search}`;
    const imageUrl = `${window.location.origin}${SHARED_IMAGE_PATH}`;

    document.title = copy.title;

    upsertMeta("name", "description", copy.description);
    upsertMeta("property", "og:title", copy.title);
    upsertMeta("property", "og:description", copy.description);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:url", canonicalUrl);

    upsertMeta("name", "twitter:title", copy.title);
    upsertMeta("name", "twitter:description", copy.description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:domain", window.location.hostname);

    upsertCanonical(canonicalUrl);

    // Signal to the prerender service that all meta tags are in place and the
    // page is ready to be captured.
    (window as Window & { prerenderReady?: boolean }).prerenderReady = true;
  }, [pathname, search, t]);

  // SPA virtual pageview voor GTM. Aparte effect met enkel pathname/search als
  // deps zodat een taalwissel geen extra pageview triggert. Loopt ná het
  // effect hierboven, dus document.title is al bijgewerkt.
  useEffect(() => {
    trackPageView(`${pathname}${search}`, document.title);
  }, [pathname, search]);

  return null;
}

